import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { login, fetchCurrentUser } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  loginId: z.string().min(1, 'Merchant Email is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { loginAction } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      loginId: '',
      password: '',
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const response = await login(data.loginId, data.password);
      const { data: responseData } = response;

      if (responseData.status === true || responseData.success === true) {
        let token = responseData.data;

        if (token && typeof token === 'object') {
          token = token.accessToken || token.token || token.data;
        }

        try {
          const userResponse = await fetchCurrentUser(token);
          if (userResponse.data.status || userResponse.data.success) {
            const userData = userResponse.data.data;

            const authData = {
              accessToken: token,
              user: {
                id: userData.userId,
                name: userData.email.split('@')[0],
                firstName: responseData.data.firstName,
                lastName: responseData.data.lastName,
                email: userData.email,
                role: userData.role,
                permissions: userData.permissions || [],
                merchantId: userData.merchantId,
                accountId: userData.accountId
              }
            };

            loginAction(authData);
            toast.success('Login successful');
            navigate('/dashboard');
          } else {
            toast.error(userResponse.data.message || 'Failed to fetch user profile');
          }
        } catch (userErr) {
          toast.error(userErr.response?.data?.message || 'Error fetching user profile');
        }
      } else {
        toast.error(responseData.message || 'Login failed');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.description || 'An error occurred during login.';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

      <div className="w-full max-w-md p-8 sm:p-10 space-y-8 glass rounded-2xl z-10 animate-slide-up m-4">
        <div className="text-center space-y-2">
          <img src="/icon.png" alt="Waya" className="w-12 h-12 mx-auto mb-6 object-contain drop-shadow-lg" />
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Merchant Portal</h2>
          <p className="text-slate-500 text-sm">Sign in to manage your B2B account</p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Email"
            id="loginId"
            type="text"
            placeholder="Enter your merchant email"
            {...register('loginId')}
            error={errors.loginId?.message}
          />

          <Input
            label="Password"
            id="password"
            type="password"
            placeholder="••••••••"
            {...register('password')}
            error={errors.password?.message}
          />

          <div className="flex items-center justify-between pt-2">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              Sign In
            </Button>
          </div>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Forgot Password?
            </button>
          </div>

          <div className="text-center pt-4 border-t border-slate-100 italic">
            <a href="/documentation" className="text-xs font-medium text-slate-400 hover:text-primary transition-colors">
              View API Documentation
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
